import React, { useState } from "react";
import useSWR from "swr";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft, Phone, Mail, MessageCircle, Building2 } from "lucide-react";
import { api, fmtINR, fmtDate } from "@/lib/api";
import { Skeleton, Pill, StatusPill, PriorityDot, ProgressRing } from "@/components/Atoms";

const fetcher = (u) => api.get(u).then((r) => r.data);

const TABS = [
  { id: "financials", label: "Financials" },
  { id: "progress",   label: "Progress" },
  { id: "documents",  label: "Documents" },
  { id: "tickets",    label: "Tickets" },
];

export default function CRMCustomerDetail() {
  const { id } = useParams();
  const { data } = useSWR(`/customers/${id}`, fetcher);
  const [tab, setTab] = useState("financials");

  if (!data) return <Skeleton h={400}/>;
  const c = data.customer;
  const fin = data.financials;

  return (
    <div className="space-y-5">
      <Link to="/crm/customers" className="rc-btn rc-btn-sm rc-btn-ghost -ml-3"><ChevronLeft size={16}/> Customers</Link>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-5">
        <aside className="space-y-3 lg:sticky lg:top-4 self-start">
          <div className="rc-card rc-card-padded">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-full flex items-center justify-center text-white text-lg" style={{ background: "var(--rc-blue)" }}>
                {(c.customer_name || "").split(" ").map(s => s[0]).slice(0,2).join("")}
              </div>
              <div className="min-w-0">
                <div className="rc-headline truncate">{c.customer_name}</div>
                <div className="rc-footnote">{c.title_name}</div>
              </div>
            </div>
            <div className="mt-4 space-y-2 rc-footnote">
              <div className="flex items-center gap-2"><Phone size={14}/>{c.primary_phone || "—"}</div>
              <div className="flex items-center gap-2"><Mail size={14}/>{c.email || "—"}</div>
              {data.flat && (
                <div className="flex items-center gap-2"><Building2 size={14}/>{data.project?.project_name} · {data.subproject?.subproject_name} · {data.flat?.flat_number}</div>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              <a href={`tel:${c.primary_phone}`} className="rc-btn rc-btn-sm rc-btn-secondary"><Phone size={14}/>Call</a>
              <a href={`mailto:${c.email}`} className="rc-btn rc-btn-sm rc-btn-secondary"><Mail size={14}/>Email</a>
              <a href={`https://wa.me/91${c.primary_phone}`} target="_blank" rel="noreferrer" className="rc-btn rc-btn-sm rc-btn-secondary"><MessageCircle size={14}/>WhatsApp</a>
            </div>
          </div>

          {fin && (
            <div className="rc-card rc-card-padded">
              <div className="rc-caption uppercase tracking-wider">Financial summary</div>
              <div className="mt-2 grid grid-cols-3 gap-2">
                <div><div className="rc-footnote">Demand</div><div className="rc-headline rc-tabular">{fmtINR(fin.summary.total_demand)}</div></div>
                <div><div className="rc-footnote">Paid</div><div className="rc-headline rc-tabular" style={{ color: "var(--rc-success)" }}>{fmtINR(fin.summary.total_paid)}</div></div>
                <div><div className="rc-footnote">Due</div><div className="rc-headline rc-tabular" style={{ color: fin.summary.outstanding > 0 ? "var(--rc-orange)" : "var(--rc-success)" }}>{fmtINR(fin.summary.outstanding)}</div></div>
              </div>
            </div>
          )}
        </aside>

        <div className="space-y-4">
          <div className="flex gap-2 overflow-x-auto rc-noscroll">
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} className={`rc-btn rc-btn-sm ${tab===t.id?"rc-btn-primary":"rc-btn-secondary"}`} data-testid={`c360-tab-${t.id}`}>{t.label}</button>
            ))}
          </div>

          {tab === "financials" && (
            <div className="rc-card rc-card-padded">
              <div className="rc-title-3 mb-3">Demand schedule</div>
              <table className="w-full text-left rc-tabular">
                <thead><tr className="rc-caption uppercase"><th className="py-2 pr-3">Milestone</th><th className="py-2 pr-3">Due</th><th className="py-2 pr-3 text-right">Amount</th><th className="py-2 pr-3">Status</th></tr></thead>
                <tbody>
                  {fin.demands.map((d, i) => (
                    <tr key={i} className="border-t border-[var(--rc-line)]">
                      <td className="py-2 pr-3">{d.milestone}</td>
                      <td className="py-2 pr-3">{fmtDate(d.due_date)}</td>
                      <td className="py-2 pr-3 text-right">{fmtINR(d.amount)}</td>
                      <td className="py-2 pr-3"><StatusPill status={d.status}/></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "progress" && (
            <div className="rc-card rc-card-padded flex flex-col md:flex-row gap-4 items-center">
              <ProgressRing value={data.milestones?.length ? Math.round(data.milestones.reduce((a,m)=>a+(m.completion_pct||0),0)/data.milestones.length) : 0} size={140}/>
              <div className="flex-1 w-full">
                <div className="rc-title-3 mb-2">Construction milestones</div>
                <div className="divide-y divide-[var(--rc-line)]">
                  {data.milestones?.map(m => (
                    <div key={m.milestone_id} className="py-2 flex items-center justify-between gap-2">
                      <div>
                        <div className="rc-body">{m.milestone_name}</div>
                        <div className="rc-caption">Planned {fmtDate(m.planned_date)}</div>
                      </div>
                      <div className="rc-headline rc-tabular">{m.completion_pct}%</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === "documents" && (
            <div className="rc-card rc-card-padded">
              <div className="rc-title-3 mb-3">Documents</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {data.documents.map(d => (
                  <div key={d.document_id} className="rc-card rc-card-padded">
                    <div className="rc-headline">{d.document_type}</div>
                    <div className="rc-footnote">{d.uploaded_at ? `Uploaded ${fmtDate(d.uploaded_at)}` : "Pending"}</div>
                    <div className="mt-2"><StatusPill status={d.status || (d.document_url ? "available" : "pending")}/></div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "tickets" && (
            <div className="rc-card rc-card-padded">
              <div className="rc-title-3 mb-3">Tickets</div>
              <div className="divide-y divide-[var(--rc-line)]">
                {data.tickets.map(t => (
                  <Link key={t.ticket_id} to={`/crm/tickets/${t.ticket_id}`} className="py-3 flex items-center gap-3 hover:bg-[var(--rc-near-white)] -mx-2 px-2 rounded-lg">
                    <PriorityDot priority={t.priority}/>
                    <div className="flex-1 min-w-0"><div className="rc-headline truncate">{t.subject}</div><div className="rc-caption">{t.category}</div></div>
                    <StatusPill status={t.sla_breached ? "breached" : t.status}/>
                  </Link>
                ))}
                {data.tickets.length === 0 && <div className="rc-footnote py-6 text-center">No tickets.</div>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
